if (!customElements.get('vn-craft-film')) {
  customElements.define('vn-craft-film', class extends HTMLElement {
    connectedCallback() {
      this.videos = [...this.querySelectorAll('video')];
      if (!this.videos.length) return;

      this.abortController = new AbortController();
      const { signal } = this.abortController;
      this.breakpoint = window.matchMedia('(max-width: 767px)');
      this.reducedMotion = window.matchMedia('(prefers-reduced-motion: reduce)');
      this.userPaused = false;
      this.userStarted = false;
      this.soundOn = false;
      this.inView = !('IntersectionObserver' in window);
      this.playButton = this.querySelector('[data-craft-play]');
      this.soundButton = this.querySelector('[data-craft-sound]');

      this.videos.forEach(video => {
        video.controls = false;
        video.muted = true;
        video.playsInline = true;
        video.addEventListener('play', () => this.updateControls(), { signal });
        video.addEventListener('pause', () => this.updateControls(), { signal });
      });
      this.querySelector('.vn-craft__controls').hidden = false;

      this.playButton.addEventListener('click', () => {
        const video = this.activeVideo();
        this.userPaused = !video.paused;
        if (this.userPaused) {
          video.pause();
        } else {
          this.userStarted = true;
          video.play().catch(() => this.updateControls());
        }
      }, { signal });

      this.soundButton?.addEventListener('click', () => {
        this.soundOn = !this.soundOn;
        this.activeVideo().muted = !this.soundOn;
        this.updateControls();
      }, { signal });

      this.breakpoint.addEventListener('change', () => this.syncPlayback(), { signal });
      this.reducedMotion.addEventListener('change', () => this.syncPlayback(), { signal });
      document.addEventListener('visibilitychange', () => this.syncPlayback(), { signal });
      if ('IntersectionObserver' in window) {
        this.observer = new IntersectionObserver(entries => {
          this.inView = entries[0].isIntersecting;
          this.syncPlayback();
        }, { threshold: 0.15 });
        this.observer.observe(this.querySelector('.vn-craft__media'));
      }
      this.syncPlayback();
    }

    activeVideo() {
      const slot = this.querySelector(this.breakpoint.matches ? '[data-craft-mobile]' : '[data-craft-desktop]');
      return slot?.querySelector('video') || this.videos[0];
    }

    syncPlayback() {
      const active = this.activeVideo();
      const shouldPlay = this.inView && !document.hidden && !this.userPaused &&
        (!this.reducedMotion.matches || this.userStarted);
      this.videos.forEach(video => {
        video.muted = video !== active || !this.soundOn;
        if (video === active && shouldPlay) {
          video.play().catch(() => this.updateControls());
        } else {
          video.pause();
        }
      });
      this.updateControls();
    }

    updateControls() {
      const video = this.activeVideo();
      const playing = !video.paused;
      this.toggleAttribute('data-playing', playing);
      this.toggleAttribute('data-sound-on', !video.muted);
      this.playButton.setAttribute('aria-label', playing ? 'Pause video' : 'Play video');
      this.querySelector('[data-craft-play-label]').textContent = playing ? 'Pause' : 'Play';
      if (this.soundButton) {
        this.soundButton.setAttribute('aria-label', video.muted ? 'Turn sound on' : 'Mute video');
        this.soundButton.setAttribute('aria-pressed', String(!video.muted));
        this.querySelector('[data-craft-sound-label]').textContent = video.muted ? 'Sound off' : 'Sound on';
      }
    }

    disconnectedCallback() {
      this.observer?.disconnect();
      this.abortController?.abort();
      this.videos?.forEach(video => video.pause());
    }
  });
}
